import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Conversation, ConversationParticipantRef, Message } from '../types/Conversation';
import { filterMessagesForCommentVisibility } from '../utils/instagramComposerUtils';
import { idsEqual, resolveConversationMeSender } from '../utils/messageUtils';
import { maybeMaskChannelIdentitiesInText } from '../../contacts/contactChannelUtils';
import { useContactChannelMasking } from '../../contacts/useContactChannelMasking';

const getParticipantKey = (participant: ConversationParticipantRef): string => (
  `${participant.type}:${participant.id}`.trim().toLowerCase()
);

const resolveServerUnreadCount = (
  unreadCountByParticipant: Record<string, number> | undefined,
  meSender: ConversationParticipantRef | undefined,
): number | undefined => {
  if (!unreadCountByParticipant || !meSender) {
    return undefined;
  }

  const directKey = getParticipantKey(meSender);
  if (typeof unreadCountByParticipant[directKey] === 'number') {
    return unreadCountByParticipant[directKey];
  }

  const matchedEntry = Object.entries(unreadCountByParticipant).find(([key]) => key.trim().toLowerCase() === directKey);
  return matchedEntry ? matchedEntry[1] : undefined;
};

interface ConversationNotificationEvent {
  conversationId: string;
  type: 'message_received' | 'added_to_conversation' | 'incoming_call';
  title: string;
  body: string;
  messageId: string | null;
  timestamp: string;
}

interface UseConversationNotificationsOptions {
  list: Conversation[];
  hiddenList: Conversation[];
  selectedConversationId?: string | null;
  selectedConversation?: Conversation | null;
  showCommentThreads: boolean;
  accessToken?: string;
  employeeId?: string | null;
  tenantId?: string;
  onOpenConversationFromNotification: (conversationId: string) => void;
}

interface UseConversationNotificationsResult {
  notificationPermission: NotificationPermission;
  isSoundEnabled: boolean;
  unreadCountByConversationId: Record<string, number>;
  totalUnreadConversationCount: number;
  playNotificationSound: () => Promise<void>;
  onToggleSound: () => void;
  handleConversationNotification: (evt: ConversationNotificationEvent) => void;
  applyListItemUnreadCounts: (conversationId: string, unreadCountByParticipant: Record<string, number>) => void;
}

export const useConversationNotifications = ({
  list,
  hiddenList,
  selectedConversationId,
  selectedConversation,
  showCommentThreads,
  accessToken,
  employeeId,
  tenantId,
  onOpenConversationFromNotification,
}: UseConversationNotificationsOptions): UseConversationNotificationsResult => {
  const maskChannelValues = useContactChannelMasking();
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');
  const [isSoundEnabled, setIsSoundEnabled] = useState(true);
  const [serverUnreadByConversationId, setServerUnreadByConversationId] = useState<Record<string, Record<string, number>>>({});
  const defaultDocumentTitleRef = useRef<string>(document.title);
  const recentNotificationKeysRef = useRef<Map<string, number>>(new Map());
  const notificationSound = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          setNotificationPermission(permission);
        });
      }
    }
  }, []);

  useEffect(() => {
    const audio = new Audio('/notification.mp3');
    audio.volume = 0.7;
    audio.preload = 'auto';
    notificationSound.current = audio;

    return () => {
      notificationSound.current = null;
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => undefined);
        audioContextRef.current = null;
      }
    };
  }, []);

  const playFallbackBeep = useCallback(async () => {
    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) return;

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextCtor();
    }

    const ctx = audioContextRef.current;
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = 880;
    gainNode.gain.value = 0.06;
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.14);
  }, []);

  const playNotificationSound = useCallback(async () => {
    const audio = notificationSound.current;
    if (audio) {
      try {
        audio.currentTime = 0;
        await audio.play();
        return;
      } catch {
      }
    }

    try {
      await playFallbackBeep();
    } catch {
    }
  }, [playFallbackBeep]);

  const applyListItemUnreadCounts = useCallback((conversationId: string, unreadCountByParticipant: Record<string, number>) => {
    setServerUnreadByConversationId(current => ({
      ...current,
      [conversationId]: unreadCountByParticipant,
    }));
  }, []);

  const countUnreadMessages = useCallback((
    messages: Message[],
    conversationMeSender: ConversationParticipantRef,
  ): number => {
    const visibleMessages = filterMessagesForCommentVisibility(messages, showCommentThreads);

    return visibleMessages.reduce((count, message) => {
      const isOwnMessage = message.sender.type === conversationMeSender.type
        && idsEqual(message.sender.id, conversationMeSender.id);
      if (isOwnMessage) {
        return count;
      }

      const isRead = message.readBy.some(reader => (
        reader.type === conversationMeSender.type && idsEqual(reader.id, conversationMeSender.id)
      ));

      return isRead ? count : count + 1;
    }, 0);
  }, [showCommentThreads]);

  const unreadCountByConversationId = useMemo(() => {
    const unreadCounts: Record<string, number> = {};
    const allConversations = [...list, ...hiddenList];

    allConversations.forEach(conversation => {
      const sourceConversation = selectedConversation && idsEqual(selectedConversation.id, conversation.id)
        ? selectedConversation
        : conversation;
      const conversationMeSender = resolveConversationMeSender(
        sourceConversation,
        accessToken,
        tenantId,
        employeeId,
      );

      if (!conversationMeSender) {
        unreadCounts[conversation.id] = 0;
        return;
      }

      const messageBasedUnreadCount = countUnreadMessages(
        sourceConversation.messages,
        conversationMeSender,
      );

      const isActiveConversation = Boolean(
        selectedConversation && idsEqual(selectedConversation.id, conversation.id),
      );

      const hasLoadedMessages = sourceConversation.messages.length > 0;

      if (isActiveConversation || hasLoadedMessages || !showCommentThreads) {
        unreadCounts[conversation.id] = messageBasedUnreadCount;
        return;
      }

      const serverUnreadCount = resolveServerUnreadCount(
        serverUnreadByConversationId[conversation.id],
        conversationMeSender,
      );

      unreadCounts[conversation.id] = typeof serverUnreadCount === 'number'
        ? serverUnreadCount
        : messageBasedUnreadCount;
    });

    return unreadCounts;
  }, [accessToken, countUnreadMessages, employeeId, hiddenList, list, selectedConversation, serverUnreadByConversationId, showCommentThreads, tenantId]);

  const totalUnreadConversationCount = useMemo(() => {
    return Object.values(unreadCountByConversationId).reduce((total, count) => total + count, 0);
  }, [unreadCountByConversationId]);

  useEffect(() => {
    const baseTitle = defaultDocumentTitleRef.current;
    document.title = totalUnreadConversationCount > 0
      ? `(${totalUnreadConversationCount}) ${baseTitle}`
      : baseTitle;
  }, [totalUnreadConversationCount]);

  const shouldShowDesktopNotification = useCallback((conversationId: string) => {
    const isVisible = document.visibilityState === 'visible';
    const hasFocus = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
    const isActiveConversation = selectedConversationId ? idsEqual(selectedConversationId, conversationId) : false;
    return !(isVisible && hasFocus && isActiveConversation);
  }, [selectedConversationId]);

  const maybeShowBrowserNotification = useCallback((params: {
    title: string;
    body: string;
    conversationId: string;
  }) => {
    if (!('Notification' in window) || notificationPermission !== 'granted') {
      return;
    }

    if (!shouldShowDesktopNotification(params.conversationId)) {
      return;
    }

    const notification = new Notification(params.title, {
      body: params.body,
      tag: `conversation:${params.conversationId}`,
    });

    notification.onclick = () => {
      notification.close();
      onOpenConversationFromNotification(params.conversationId);
    };
  }, [notificationPermission, onOpenConversationFromNotification, shouldShowDesktopNotification]);

  const handleConversationNotification = useCallback((evt: ConversationNotificationEvent) => {
    const dedupeKey = `${evt.type}:${evt.conversationId}:${evt.messageId || evt.timestamp}`;
    const now = Date.now();

    for (const [key, value] of Array.from(recentNotificationKeysRef.current.entries())) {
      if (now - value > 15000) {
        recentNotificationKeysRef.current.delete(key);
      }
    }

    if (recentNotificationKeysRef.current.has(dedupeKey)) {
      return;
    }

    recentNotificationKeysRef.current.set(dedupeKey, now);

    if (shouldShowDesktopNotification(evt.conversationId) && isSoundEnabled) {
      void playNotificationSound();
    }

    maybeShowBrowserNotification({
      title: maybeMaskChannelIdentitiesInText(
        evt.title || (evt.type === 'added_to_conversation' ? 'Added to conversation' : 'New message'),
        maskChannelValues,
      ),
      body: maybeMaskChannelIdentitiesInText(evt.body, maskChannelValues),
      conversationId: evt.conversationId,
    });
  }, [isSoundEnabled, maskChannelValues, maybeShowBrowserNotification, playNotificationSound, shouldShowDesktopNotification]);

  const onToggleSound = useCallback(() => {
    const next = !isSoundEnabled;
    setIsSoundEnabled(next);
    if (next) {
      void playNotificationSound();
    }
  }, [isSoundEnabled, playNotificationSound]);

  return {
    notificationPermission,
    isSoundEnabled,
    unreadCountByConversationId,
    totalUnreadConversationCount,
    playNotificationSound,
    onToggleSound,
    handleConversationNotification,
    applyListItemUnreadCounts,
  };
};